import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { isZustandDevtoolsEnabled } from "@app/store/zustandDevtoolsEnabled";
import {
    DEFAULT_NOTIFICATION_DURATION_MS,
    MAX_NOTIFICATION_COUNT,
    MIN_MS_BETWEEN_NOTIFICATIONS,
} from "./notification.config";
import type { NotificationData, NotificationPayload } from "./types";

interface NotificationState {
    notifications: NotificationData[];
    lastAddedAt: number;
}

interface NotificationActions {
    addNotification: (payload: NotificationPayload) => void;
    removeNotification: (id: string) => void;
    clearNotifications: () => void;
    closeNotification: (id: string) => void;
}

function resolveDuration(duration: NotificationPayload["duration"]): number | "infinite" {
    if (duration === "infinite") {
        return "infinite";
    }
    if (typeof duration === "number" && duration > 0) {
        return duration;
    }
    return DEFAULT_NOTIFICATION_DURATION_MS;
}

const useNotificationStore = create<NotificationState & NotificationActions>()(
    devtools(
        (set, get) => ({
            notifications: [],
            lastAddedAt: 0,
            addNotification: (payload) => {
                if (payload.message.length === 0) {
                    return;
                }

                const now = Date.now();
                const { notifications, lastAddedAt } = get();

                if (notifications.length >= MAX_NOTIFICATION_COUNT || now - lastAddedAt < MIN_MS_BETWEEN_NOTIFICATIONS) {
                    return;
                }

                const id = uuidv4();
                const entry: NotificationData = {
                    id,
                    message: payload.message,
                    type: payload.type,
                    timestamp: now,
                    duration: resolveDuration(payload.duration),
                    fromId: payload.fromId,
                    open: false,
                };

                set({
                    notifications: [...notifications, entry],
                    lastAddedAt: now,
                });

                requestAnimationFrame(() => {
                    set((state) => ({
                        notifications: state.notifications.map((n) => (n.id === id ? { ...n, open: true } : n)),
                    }));
                });
            },
            removeNotification: (id) => {
                set((state) => ({
                    notifications: state.notifications.filter((n) => n.id !== id),
                }));
            },
            clearNotifications: () => {
                set({ notifications: [], lastAddedAt: 0 });
            },
            closeNotification: (id) => {
                set((state) => ({
                    notifications: state.notifications.map((n) => (n.id === id ? { ...n, open: false } : n)),
                }));
            },
        }),
        { name: "app-notification-store", enabled: isZustandDevtoolsEnabled },
    ),
);

export { useNotificationStore };

/** Императивное добавление уведомления вне React. */
export function addNotificationToStore(payload: NotificationPayload): void {
    useNotificationStore.getState().addNotification(payload);
}
